/*
  Featured products slider: native scroll-snap track with prev/next arrows,
  a progress bar and a "1-4 of 8" count. Shopper-controlled only (no
  autoplay). Arrows and progress hide when every card fits.
*/
if (!customElements.get('featured-products-slider')) {
  class FeaturedProductsSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fps-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-fps-slide]'));
      this.prev = this.querySelector('[data-fps-prev]');
      this.next = this.querySelector('[data-fps-next]');
      this.controls = this.querySelector('[data-fps-controls]');
      this.footer = this.querySelector('[data-fps-footer]');
      this.progress = this.querySelector('[data-fps-progress]');
      this.count = this.querySelector('[data-fps-count]');
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.update = this.update.bind(this);
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };

      this.prev.addEventListener('click', () => this.go(-1));
      this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.update);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.update);
    }

    step() {
      // Scroll by the number of whole cards in view (at least one)
      const first = this.slides[0];
      if (!first) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const cardWidth = first.getBoundingClientRect().width + gap;
      const whole = Math.max(1, Math.floor((this.track.clientWidth + gap) / cardWidth));
      return whole * cardWidth;
    }

    go(direction) {
      this.track.scrollBy({
        left: direction * this.step(),
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    visibleRange() {
      const box = this.track.getBoundingClientRect();
      let first = -1;
      let last = -1;
      let most = 0;
      let mostShown = -1;
      this.slides.forEach((slide, index) => {
        const r = slide.getBoundingClientRect();
        const shown = Math.max(0, Math.min(r.right, box.right) - Math.max(r.left, box.left)) / r.width;
        // Count a card once at least 90% of it is showing
        if (shown >= 0.9) {
          if (first === -1) first = index;
          last = index;
        }
        if (shown > mostShown) {
          mostShown = shown;
          most = index;
        }
      });
      // Mid-swipe (e.g. on phones, where only one card fits) no card may be
      // 90% in view, so fall back to whichever card shows the most rather
      // than jumping back to the first one
      if (first === -1) first = last = most;
      return [first + 1, last + 1, most + 1];
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const scrollable = max > 2;
      this.controls.hidden = !scrollable;
      this.footer.hidden = !scrollable;
      if (!scrollable) return;

      const left = Math.abs(this.track.scrollLeft);
      this.prev.disabled = left <= 2;
      this.next.disabled = left >= max - 2;

      const total = this.slides.length;
      const [first, last, most] = this.visibleRange();
      const visibleShare = this.track.clientWidth / this.track.scrollWidth;
      const position = max > 0 ? left / max : 0;
      this.progress.style.width = (visibleShare * 100).toFixed(2) + '%';
      this.progress.style.transform = 'translateX(' + (position * (1 / visibleShare - 1) * 100).toFixed(2) + '%)';

      if (this.mobile.matches) {
        // Phones: the card that's mostly in view, so the number moves on as
        // soon as the next card takes over during a swipe
        this.count.textContent = most + ' / ' + total;
      } else if (first === last) {
        this.count.textContent = first + ' / ' + total;
      } else {
        this.count.textContent = first + '–' + last + ' of ' + total;
      }
    }
  }

  customElements.define('featured-products-slider', FeaturedProductsSlider);
}
